import { useEffect, useMemo, useState } from 'react';

import { SearchOutlined } from '@ant-design/icons';
import { Flex, message, Select } from 'antd';
import clsx from 'classnames';
import { useShallow } from 'zustand/shallow';

import { SPACE } from '@/shared/design-tokens/design-tokens';
import { FilterDropdown } from '@/shared/ui/FilterDropdown';
import type { FilterDropdownOption } from '@/shared/ui/FilterDropdown';

import { useStatsTablesQuery } from '@/entities/quality';
import { dataZoneLowerCaseSchema } from '@/entities/quality/domain/type';

import { FILTER_CONFIGS, type FilterKey, type FilterValueType } from '@/features/quality/lib';
import {
  assetQualityFilterStoreActions,
  type AssetQualityTabValue,
  useAssetQualityFilterStore,
} from '@/features/quality/model';

import styles from './AssetQualityFilter.module.css';

const convertToFilterOptions = <T extends string | number>(
  options: { label: string; value: T }[]
): FilterDropdownOption<T>[] => {
  return options.map((option) => ({
    key: String(option.value),
    label: option.label,
    value: option.value,
    disabled: false,
  }));
};

const renderListItem = (option: FilterDropdownOption<string | number>) => (
  <div className={styles.listContent}>
    <span>{option.label}</span>
  </div>
);

const renderTableListItem = (key: string, label: string, value: string) => (
  <div className={styles.tableOption} key={key}>
    <div className={clsx(styles.tableTextBase, styles.tableFqn)}>{label}</div>
    <div className={clsx(styles.tableTextBase, styles.tableName)}>{value}</div>
  </div>
);

const createFilterChangeHandlers = <T extends string | number>(
  tabValue: AssetQualityTabValue
): Record<FilterKey, (value: T) => void> => ({
  dataZone: (value) =>
    assetQualityFilterStoreActions.setSelectedDataZoneValue(tabValue, value as FilterValueType['dataZone']),
  dataQuality: (value) =>
    assetQualityFilterStoreActions.setSelectedDataQualityValue(tabValue, value as FilterValueType['dataQuality']),
  dateRange: (value) =>
    assetQualityFilterStoreActions.setSelectedDateRangeValue(tabValue, value as FilterValueType['dateRange']),
  sortBy: (value) =>
    assetQualityFilterStoreActions.setSelectedSortByValue(tabValue, value as FilterValueType['sortBy']),
});

type AssetQualityFilterProps = {
  selectedAssetTableTabValue: AssetQualityTabValue;
};

export const AssetQualityFilter = ({ selectedAssetTableTabValue }: AssetQualityFilterProps) => {
  const { data: statsTables } = useStatsTablesQuery({ sortBy: 'tableName' });

  const [tableSearchValue, setTableSearchValue] = useState('');
  const [columnSearchValue, setColumnSearchValue] = useState('');

  const { table, column } = useAssetQualityFilterStore(
    useShallow((state) => ({ table: state.table, column: state.column }))
  );

  const filterState = selectedAssetTableTabValue === 'table' ? table : column;

  const {
    selectedTableValue,
    selectedColumnNameValue,
    selectedDataZoneValue,
    selectedDataQualityValue,
    selectedDateRangeValue,
    selectedSortByValue,
  } = filterState;

  const isDisabledDataZoneFilter = selectedTableValue !== 'All';

  const tableOptions = useMemo(() => {
    if (!statsTables?.data) return [];
    return [
      {
        label: renderTableListItem('all', '전체', '전체'),
        displayLabel: '전체',
        value: 'All',
        columns: undefined,
      },
      ...statsTables.data.map((table) => ({
        label: renderTableListItem(table.id.toString(), table.fqn, table.tableName),
        displayLabel: table.tableName,
        value: table.id.toString(),
        columns: table.columns,
      })),
    ];
  }, [statsTables]);

  const columnOptions = useMemo(() => {
    if (!selectedTableValue) return [];

    const findColumnOptions = tableOptions.find((option) => option.value === selectedTableValue)?.columns || [];
    return [
      {
        label: '전체',
        value: 'All',
      },
      ...findColumnOptions.map((column) => ({
        label: column,
        value: column,
      })),
    ];
  }, [selectedTableValue, tableOptions]);

  const filterOptions = useMemo(
    () =>
      FILTER_CONFIGS.reduce(
        (acc, config) => {
          acc[config.key] = convertToFilterOptions(config.options);
          return acc;
        },
        {} as Record<FilterKey, FilterDropdownOption<string | number>[]>
      ),
    []
  );

  const filterStateValues: Record<FilterKey, string | number> = {
    dataZone: selectedDataZoneValue,
    dataQuality: selectedDataQualityValue,
    dateRange: selectedDateRangeValue,
    sortBy: selectedSortByValue,
  };

  const filterChangeHandlers = createFilterChangeHandlers(selectedAssetTableTabValue);

  useEffect(() => {
    const selectedTable = statsTables?.data?.find((table) => table.id.toString() === selectedTableValue);
    const parseResult = dataZoneLowerCaseSchema.safeParse(selectedTable?.dataZone);
    const dataZoneValue = selectedTableValue !== 'All' && parseResult.success ? parseResult.data : 'All';
    assetQualityFilterStoreActions.setSelectedDataZoneValue(selectedAssetTableTabValue, dataZoneValue);
  }, [selectedAssetTableTabValue, selectedTableValue, statsTables?.data]);

  return (
    <Flex className={styles.assetQualityCont} gap={SPACE[8]} wrap>
      <Select
        className={styles.tableSelect}
        prefix={<SearchOutlined />}
        value={selectedTableValue}
        onChange={(value) =>
          assetQualityFilterStoreActions.setSelectedTableValue(selectedAssetTableTabValue, value || 'All')
        }
        onSearch={(value) => setTableSearchValue(value)}
        options={tableOptions}
        optionLabelProp="displayLabel"
        optionFilterProp="displayLabel"
        placeholder="테이블 검색..."
        virtual
        showSearch
        allowClear
        notFoundContent={
          tableSearchValue ? `"${tableSearchValue}"에 대한 검색 결과가 없습니다` : '검색 결과가 없습니다'
        }
      />
      {selectedAssetTableTabValue === 'column' && selectedTableValue !== 'All' && (
        <Select
          className={styles.tableSelect}
          prefix={<SearchOutlined />}
          value={selectedColumnNameValue}
          onChange={(value) =>
            assetQualityFilterStoreActions.setSelectedColumnNameValue(selectedAssetTableTabValue, value || 'All')
          }
          onSearch={(value) => setColumnSearchValue(value)}
          options={columnOptions}
          virtual
          showSearch
          allowClear
          notFoundContent={
            columnSearchValue ? `"${columnSearchValue}"에 대한 검색 결과가 없습니다` : '검색 결과가 없습니다'
          }
        />
      )}
      {FILTER_CONFIGS.map((config) => {
        return (
          <div
            key={config.key}
            onClick={() => {
              if (config.key === 'dataZone' && isDisabledDataZoneFilter) {
                message.error('테이블을 선택했을 때는 해당 필터를 수정할 수 없습니다');
                return;
              }
            }}>
            <FilterDropdown<string | number>
              buttonText={config.buttonText}
              searchPlaceholder={`${config.buttonText} 검색...`}
              selectedValues={[filterStateValues[config.key]]}
              options={filterOptions[config.key]}
              onSelectionChange={(values) => filterChangeHandlers[config.key](values[0])}
              renderItem={renderListItem}
              mode="single"
              width={config.width}
              showSearch={false}
              disabled={config.key === 'dataZone' ? isDisabledDataZoneFilter : undefined}
            />
          </div>
        );
      })}
    </Flex>
  );
};
